import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Guess {
  name: string;
  km: number;
  dir: string;
}

interface View {
  coa: string;
  guesses: Guess[];
  maxGuesses: number;
  answer?: { name: string; pop: number };
  credit?: string;
}

const COPY = {
  unknown_city: "Не знаю такого міста — вибери з підказок",
  already_guessed: "Вже було",
};

export function Gerb({ date }: { date?: string | undefined }) {
  const g = useGame<View>("gerb", COPY, date);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("gerb").then((r) => setNames(r.names)).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.name) ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const left = v.maxGuesses - v.guesses.length;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ГЕРБ ДНЯ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mx-auto mt-1 flex h-[34vh] w-full max-w-[300px] items-center justify-center rounded-2xl bg-white/90 p-4">
          <img src={v.coa} alt="герб міста" className="max-h-full max-w-full object-contain" referrerPolicy="no-referrer" />
        </div>
        <div className="mt-2 text-center text-xs text-tghint">
          {finished ? "герб з Wikimedia Commons" : `яке це місто? спроб лишилось: ${left}`}
        </div>

        {v.guesses.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {[...v.guesses].reverse().map((x) => (
              <li
                key={x.name}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm ${x.km === 0 ? "bg-ok/15" : "bg-tgsection"}`}
              >
                <span className="font-medium">{x.name}</span>
                <span className="text-tghint">{x.km === 0 ? "🎉 точно" : `${x.km} км ${x.dir}`}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <SuggestInput
          names={names}
          disabled={finished}
          busy={g.busy}
          exclude={guessed}
          onSubmit={(name) => void g.run(() => api.guess<View>("gerb", name, date))}
          placeholder="назва міста"
        />
        {!finished && v.guesses.length >= 2 && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("gerb", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Геральдист!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-lg font-bold">{v.answer.name}</div>
              <div className="mt-0.5 text-xs text-tghint">
                👥 {v.answer.pop >= 1e6 ? `${(v.answer.pop / 1e6).toFixed(2)} млн` : `${Math.round(v.answer.pop / 1e3)} тис`}
              </div>
              {v.credit && (
                <a href={v.credit} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-tgbutton underline">
                  герб на Commons ↗
                </a>
              )}
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
